/**
 * Avatar background visual effects system
 *
 * This hook handles visual effects that render behind the avatar balloon:
 * - Glow animation with smooth scale pulsing
 * - Future effects like particle trails, auras, sparkles, etc.
 *
 * Background effects are layered behind the avatar and don't affect the avatar's
 * layout or positioning. They're purely cosmetic enhancements that can be
 * triggered by game events or user interactions.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAvatarRAF } from './useAvatarRAF';
import { useRoomInteractionWindow } from '../../../hooks';
import type { RoomPlayer } from '@airhead-games/shared/types';

/**
 * Configuration for background effects
 */
const BG_EFFECTS_CONFIG = {
  // Glow effect settings
  GLOW: {
    DURATION_MS: 2000, // 2 second duration for each phase (expand/contract)
    MIN_SCALE: 1, // Minimum glow scale
    MAX_SCALE: 3, // Maximum glow scale
    EASE_POWER: 3, // Cubic easing power for smooth animation
  },
} as const;

/**
 * Glow animation phases
 */
type GlowPhase = 'expand' | 'contract';

/**
 * Props for useAvatarBgEffects hook
 */
interface UseAvatarBgEffectsProps {
  player?: RoomPlayer; // Player data for waiting player overlay
  enableGlow?: boolean; // Whether glow effect is enabled (base state)
  glowType?: 'pulse' | 'static'; // Preferred glow animation type (default: 'pulse')
}

/**
 * Return type for useAvatarBgEffects hook
 */
interface UseAvatarBgEffectsReturn {
  glowScale: number; // Current glow scale (1-3 range)
  glowOpacity: number; // Current glow opacity (0-1 range)
  isGlowActive: boolean; // Whether glow animation is currently active
}

/**
 * Hook for avatar background visual effects
 *
 * Manages visual effects that render behind the avatar, starting with glow
 * and extensible for future effects like particles or auras.
 *
 * @param props - Configuration for background effects
 * @returns Current effect states and values
 */
export function useAvatarBgEffects({
  player,
  enableGlow,
  glowType = 'pulse',
}: UseAvatarBgEffectsProps = {}): UseAvatarBgEffectsReturn {
  const { subscribe } = useAvatarRAF();
  const { windowStatus, windowWaitingFor } = useRoomInteractionWindow();

  // Resolve final glow state with priority:
  // 1) Explicit enableGlow prop (true/false) takes precedence
  // 2) Waiting player overlay (glow during interaction window) when enableGlow is undefined
  // 3) Default to false
  const resolvedEnableGlow =
    enableGlow === true
      ? true
      : enableGlow === false
        ? false
        : player &&
            windowStatus === 'opened' &&
            windowWaitingFor.includes(player.playerId)
          ? true
          : false;

  // Glow animation state
  const [glowScale, setGlowScale] = useState<number>(
    BG_EFFECTS_CONFIG.GLOW.MIN_SCALE
  );
  const [glowOpacity, setGlowOpacity] = useState(0);
  const [isGlowActive, setIsGlowActive] = useState(false);

  // Internal glow animation state (refs to avoid re-renders)
  const glowTimeRef = useRef(0);
  const glowPhaseRef = useRef<GlowPhase>('expand');

  /**
   * Calculate smooth easing functions
   */
  const easeOutCubic = useCallback((progress: number): number => {
    return 1 - Math.pow(1 - progress, BG_EFFECTS_CONFIG.GLOW.EASE_POWER);
  }, []);

  const easeInCubic = useCallback((progress: number): number => {
    return Math.pow(progress, BG_EFFECTS_CONFIG.GLOW.EASE_POWER);
  }, []);

  /**
   * Update glow animation
   */
  const updateGlowAnimation = useCallback(
    (deltaTime: number) => {
      if (!resolvedEnableGlow) return;

      glowTimeRef.current += deltaTime;
      const time = glowTimeRef.current;
      const duration = BG_EFFECTS_CONFIG.GLOW.DURATION_MS;
      const phase = glowPhaseRef.current;

      if (phase === 'expand') {
        // Expand from min to max scale with ease-out
        const progress = Math.min(time / duration, 1);
        const easedProgress = easeOutCubic(progress);
        const scale =
          BG_EFFECTS_CONFIG.GLOW.MIN_SCALE +
          easedProgress *
            (BG_EFFECTS_CONFIG.GLOW.MAX_SCALE -
              BG_EFFECTS_CONFIG.GLOW.MIN_SCALE);

        setGlowScale(scale);

        if (progress >= 1) {
          // Switch to contract phase
          glowPhaseRef.current = 'contract';
          glowTimeRef.current = 0;
        }
      } else {
        // Contract from max to min scale with ease-in
        const progress = Math.min(time / duration, 1);
        const easedProgress = easeInCubic(progress);
        const scale =
          BG_EFFECTS_CONFIG.GLOW.MAX_SCALE -
          easedProgress *
            (BG_EFFECTS_CONFIG.GLOW.MAX_SCALE -
              BG_EFFECTS_CONFIG.GLOW.MIN_SCALE);

        setGlowScale(scale);

        if (progress >= 1) {
          // Switch to expand phase
          glowPhaseRef.current = 'expand';
          glowTimeRef.current = 0;
        }
      }
    },
    [resolvedEnableGlow, easeOutCubic, easeInCubic]
  );

  // Handle glow enable/disable
  useEffect(() => {
    if (resolvedEnableGlow) {
      setIsGlowActive(true);
      setGlowOpacity(1);

      if (glowType === 'static') {
        // For static glow, use max scale to create a visible shadow effect
        setGlowScale(BG_EFFECTS_CONFIG.GLOW.MAX_SCALE);
      } else {
        // Start animated glow
        glowTimeRef.current = 0;
        glowPhaseRef.current = 'expand';
      }
    } else {
      // Stop glow animation and reset
      setIsGlowActive(false);
      setGlowScale(BG_EFFECTS_CONFIG.GLOW.MIN_SCALE);
      setGlowOpacity(0);
      glowTimeRef.current = 0;
      glowPhaseRef.current = 'expand';
    }
  }, [resolvedEnableGlow, glowType]);

  // Subscribe to RAF for glow animation updates
  useEffect(() => {
    if (!resolvedEnableGlow || glowType === 'static') return;

    return subscribe((deltaTime: number) => {
      updateGlowAnimation(deltaTime);
    });
  }, [subscribe, resolvedEnableGlow, glowType, updateGlowAnimation]);

  return {
    glowScale,
    glowOpacity,
    isGlowActive,
  };
}
